import { Link2, Gauge, FlaskConical,
  LayoutDashboard,
  Target,
  ListChecks,
  Users,
  Brain,
  CalendarDays,
  Mail,
  Phone,
  FileText,
  Handshake,
  CheckCircle2,
  TrendingUp,
  MessageSquare,
  Share2,
  ShoppingCart,
  ClipboardList,
  Calculator,
  BookOpen,
  FileEdit,
  Euro,
  Settings,
  ShieldCheck,
  CreditCard,
  Bot,
  Building2,
  Globe,
  Plug,
  Gift,
  Award,
  Zap,
  Radar,
  Landmark,
  Eye,
  Briefcase,
  Mic,
  Home,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "@/components/LanguageSelector";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

export function DashboardSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const currentPath = location.pathname + location.hash;
  const { t } = useTranslation();

  const mainItems = [
    { title: t("nav.dashboard"), url: "/dashboard", icon: LayoutDashboard },
    { title: "Mon Business", url: "/mon-business", icon: Building2 },
    { title: "Clients Portage (ITG)", url: "/portage-clients", icon: Briefcase },
    { title: "Voix de Grafton", url: "/voix", icon: Mic },
    { title: t("nav.briefing"), url: "/briefing", icon: ClipboardList },
    { title: t("nav.sales"), url: "/ventes", icon: ShoppingCart },
    { title: t("nav.treasury"), url: "/tresorerie", icon: Calculator },
    { title: t("nav.accounting"), url: "/comptabilite", icon: BookOpen },
    { title: t("nav.businessPlan"), url: "/business-plan", icon: FileEdit },
    { title: t("nav.financing"), url: "/financement", icon: Euro },
    { title: "Mon plan", url: "/mon-plan", icon: Target },
    { title: "Pilotage de l'objectif", url: "/pilotage", icon: Gauge },
    { title: "Liens suivis", url: "/liens-suivis", icon: Link2 },
    { title: "Validations", url: "/validations", icon: CheckCircle2 },
    { title: "La maison", url: "/maison", icon: Home },
    { title: "Affiliation", url: "/affiliation", icon: Handshake },
    { title: "Savoir-faire", url: "/savoir-faire", icon: BookOpen },
    { title: "Marchés publics", url: "/marches-publics", icon: Landmark },
    { title: "Visiteurs en direct", url: "/visiteurs-live", icon: Eye },
    { title: "Équipe & Trésorerie", url: "/equipe", icon: Users },
    { title: "Lever des fonds", url: "/lever-des-fonds", icon: Target },
    { title: "Recruter des closers", url: "/closers", icon: Handshake },
    { title: "Lead Hunters IA", url: "/lead-hunters", icon: Radar },
    { title: "Laboratoire", url: "/laboratoire", icon: FlaskConical },
    { title: t("nav.actionPlan"), url: "/dashboard#actions", icon: ListChecks },
    { title: t("nav.aiAdvice"), url: "/dashboard#ia", icon: Brain },
    { title: t("nav.agenda"), url: "/agenda", icon: CalendarDays },
  ];

  const communicationItems = [
    { title: t("nav.emails"), url: "/emails", icon: Mail },
    { title: t("nav.calls"), url: "/appels", icon: Phone },
    { title: t("nav.admin"), url: "/administratif", icon: FileText },
    { title: t("nav.partners"), url: "/partenaires", icon: Handshake },
  ];

  const marketingItems = [
    { title: "CRM", url: "/crm", icon: Users },
    { title: t("nav.marketing"), url: "/marketing", icon: TrendingUp },
    { title: t("nav.communication"), url: "/communication", icon: MessageSquare },
    { title: t("nav.social"), url: "/social", icon: Share2 },
    { title: t("nav.funnel"), url: "/tunnel-vente", icon: Target },
  ];

  const rhItems = [
    { title: t("nav.hr"), url: "/rh", icon: Users },
  ];

  const nanoCorporeItems = [
    { title: t("nav.nanocorp"), url: "/nanocorp", icon: Bot },
    { title: "Grafton Autonome", url: "/grafton-autonome", icon: Zap },
  ];

  const creationItems = [
    { title: t("nav.companyCreation"), url: "/creation-entreprise", icon: Building2 },
  ];

  const extraItems = [
    { title: "Connexions API", url: "/connexions-api", icon: Plug },
    { title: t("nav.website"), url: "/my-website", icon: Globe },
    { title: t("nav.payment"), url: "/paiement", icon: CreditCard },
    { title: t("nav.security"), url: "/securite", icon: ShieldCheck },
    { title: t("nav.offers"), url: "/offres", icon: CreditCard },
  ];

  const isActive = (url: string) => {
    if (url === "/dashboard") return location.pathname === "/dashboard" && !location.hash;
    if (url.startsWith("/dashboard#")) return currentPath === url;
    return location.pathname === url;
  };

  const renderGroup = (label: string, items: typeof mainItems) => (
    <SidebarGroup key={label}>
      {label && (
        <SidebarGroupLabel className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-semibold">
          {label}
        </SidebarGroupLabel>
      )}
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={item.url}>
              <SidebarMenuButton
                asChild
                isActive={isActive(item.url)}
                tooltip={collapsed ? item.title : undefined}
              >
                <NavLink
                  to={item.url}
                  className="gap-3 text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
                  activeClassName="bg-sidebar-accent text-primary font-medium"
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="truncate">{item.title}</span>}
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarContent className="pt-2">
        {!collapsed && (
          <div className="px-3 pb-2">
            <LanguageSelector />
          </div>
        )}
        {renderGroup("", mainItems)}
        {renderGroup(t("groups.communication"), communicationItems)}
        {renderGroup(t("groups.marketing"), marketingItems)}
        {renderGroup(t("groups.hr"), rhItems)}
        {renderGroup(t("groups.autonomous"), nanoCorporeItems)}
        {renderGroup(t("groups.creation"), creationItems)}
        {renderGroup("", extraItems)}
        {renderGroup("", [
          { title: "Compétences", url: "/competences", icon: Award },
          { title: "ADN Grafton", url: "/adn-grafton", icon: Brain },
          { title: "Parrainage", url: "/parrainage", icon: Gift },
          { title: t("nav.settings"), url: "/parametres", icon: Settings },
        ])}
      </SidebarContent>
    </Sidebar>
  );
}
